import { useState, useEffect } from "react";
import { Package, Plus, RefreshCw, AlertTriangle, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIngredients, Ingredient } from "@/hooks/useIngredients";
import { useStockMovements } from "@/hooks/useStockMovements";
import { useProductStock, StockProduct } from "@/hooks/useProductStock";
import { IngredientFormDialog } from "@/components/stock/IngredientFormDialog";
import { StockMovementDialog } from "@/components/stock/StockMovementDialog";
import { ProductStockMovementDialog } from "@/components/stock/ProductStockMovementDialog";
import { IngredientList } from "@/components/stock/IngredientList";
import { ProductStockList } from "@/components/stock/ProductStockList";
import { StockMovementsList } from "@/components/stock/StockMovementsList";
import { StockMovementsFilters } from "@/components/stock/StockMovementsFilters";
import { LowStockAlert } from "@/components/stock/LowStockAlert";

export default function EstoquePage() {
  const {
    ingredients,
    isLoading: isLoadingIngredients,
    fetchIngredients,
    createIngredient,
    updateIngredient,
    deleteIngredient,
    addStockMovement,
    getLowStockIngredients,
  } = useIngredients();

  const {
    products: stockProducts,
    isLoading: isLoadingProducts,
    fetchStockProducts,
    addProductStockMovement,
    getLowStockProducts,
    getTotalStockValue: getProductStockValue,
  } = useProductStock();

  // Movement filters state
  const [movementPage, setMovementPage] = useState(1);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [movementType, setMovementType] = useState<"entry" | "exit" | "sale" | "adjustment" | "all">("all");
  const [itemCategory, setItemCategory] = useState<"ingredient" | "product" | "all">("all");

  const { 
    movements, 
    totalMovements, 
    totalPages,
    isLoading: isLoadingMovements,
    fetchMovements, 
    getMovementTypeLabel 
  } = useStockMovements({
    page: movementPage,
    pageSize: 20,
    startDate,
    endDate,
    movementType,
    itemCategory,
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isIngredientMovementOpen, setIsIngredientMovementOpen] = useState(false);
  const [isProductMovementOpen, setIsProductMovementOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);
  const [movementIngredient, setMovementIngredient] = useState<Ingredient | null>(null);
  const [movementProduct, setMovementProduct] = useState<StockProduct | null>(null);

  const lowStockIngredients = getLowStockIngredients();
  const lowStockProducts = getLowStockProducts();

  useEffect(() => {
    fetchStockProducts();
  }, []);

  // Fetch movements when filters change
  useEffect(() => {
    fetchMovements();
  }, [movementPage, startDate, endDate, movementType, itemCategory]);

  const handleOpenForm = (ingredient?: Ingredient) => {
    setEditingIngredient(ingredient || null);
    setIsFormOpen(true);
  };

  const handleOpenIngredientMovement = (ingredient: Ingredient) => {
    setMovementIngredient(ingredient);
    setIsIngredientMovementOpen(true);
  };

  const handleOpenProductMovement = (product: StockProduct) => {
    setMovementProduct(product);
    setIsProductMovementOpen(true);
  };

  const handleSaveIngredient = async (data: {
    name: string;
    unit: string;
    current_stock?: number;
    minimum_stock?: number;
    cost_per_unit?: number;
  }) => {
    if (editingIngredient) {
      await updateIngredient(editingIngredient.id, data);
    } else {
      await createIngredient(data);
    }
    setIsFormOpen(false);
    setEditingIngredient(null);
  };

  const handleSaveIngredientMovement = async (
    type: "entry" | "exit" | "adjustment",
    quantity: number,
    reason?: string,
    cost?: number,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => {
    if (!movementIngredient) return;
    await addStockMovement(movementIngredient.id, type, quantity, reason, cost, expense);
    setIsIngredientMovementOpen(false);
    setMovementIngredient(null);
    fetchMovements();
  };

  const handleSaveProductMovement = async (
    type: "entry" | "exit" | "adjustment",
    quantity: number,
    reason?: string,
    cost?: number,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => {
    if (!movementProduct) return;
    await addProductStockMovement(movementProduct.id, type, quantity, reason, cost, expense);
    setIsProductMovementOpen(false);
    setMovementProduct(null);
    fetchMovements();
  };

  const handleRefresh = () => {
    fetchIngredients();
    fetchStockProducts();
    fetchMovements();
  };

  const handleClearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setMovementType("all");
    setItemCategory("all");
    setMovementPage(1);
  };

  const handlePageChange = (page: number) => {
    setMovementPage(page);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const ingredientInventoryValue = ingredients.reduce(
    (sum, ing) => sum + ing.current_stock * ing.cost_per_unit,
    0
  );
  const productInventoryValue = getProductStockValue();
  const totalInventoryValue = ingredientInventoryValue + productInventoryValue;
  const totalItems = ingredients.length + stockProducts.length;
  const totalLowStock = lowStockIngredients.length + lowStockProducts.length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Estoque</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie ingredientes, produtos e movimentações
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button size="sm" onClick={() => handleOpenForm()}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Ingrediente
          </Button>
        </div>
      </div>

      {/* Low Stock Alert */}
      {totalLowStock > 0 && (
        <LowStockAlert
          ingredients={lowStockIngredients}
          products={lowStockProducts}
          onAddIngredientStock={handleOpenIngredientMovement}
          onAddProductStock={handleOpenProductMovement}
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Itens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalItems}</p>
            <p className="text-xs text-muted-foreground">
              {ingredients.length} ingredientes + {stockProducts.length} produtos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Itens com Estoque Baixo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-destructive">
              {totalLowStock}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Valor em Estoque
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totalInventoryValue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ingredients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ingredients">
            Ingredientes
            {ingredients.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {ingredients.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="products">
            Produtos
            {stockProducts.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {stockProducts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="movements">
            Movimentações
            {totalMovements > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalMovements}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ingredients">
          <IngredientList
            ingredients={ingredients}
            isLoading={isLoadingIngredients}
            onEdit={handleOpenForm}
            onDelete={deleteIngredient}
            onAddMovement={handleOpenIngredientMovement}
          />
        </TabsContent>

        <TabsContent value="products">
          <ProductStockList
            products={stockProducts}
            isLoading={isLoadingProducts}
            onAddMovement={handleOpenProductMovement}
          />
        </TabsContent>

        <TabsContent value="movements" className="space-y-4">
          <StockMovementsFilters
            startDate={startDate}
            endDate={endDate}
            movementType={movementType}
            itemCategory={itemCategory}
            onStartDateChange={(date) => { setStartDate(date); setMovementPage(1); }}
            onEndDateChange={(date) => { setEndDate(date); setMovementPage(1); }}
            onMovementTypeChange={(type) => { setMovementType(type); setMovementPage(1); }}
            onItemCategoryChange={(cat) => { setItemCategory(cat); setMovementPage(1); }}
            onClearFilters={handleClearFilters}
          />
          <StockMovementsList
            movements={movements}
            getMovementTypeLabel={getMovementTypeLabel}
            isLoading={isLoadingMovements}
            currentPage={movementPage}
            totalPages={totalPages}
            totalMovements={totalMovements}
            onPageChange={handlePageChange}
          />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <IngredientFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        ingredient={editingIngredient}
        onSave={handleSaveIngredient}
      />

      <StockMovementDialog
        open={isIngredientMovementOpen}
        onOpenChange={setIsIngredientMovementOpen}
        ingredient={movementIngredient}
        onSave={handleSaveIngredientMovement}
      />

      <ProductStockMovementDialog
        open={isProductMovementOpen}
        onOpenChange={setIsProductMovementOpen}
        product={movementProduct}
        onSave={handleSaveProductMovement}
      />
    </div>
  );
}
